import { inject, IEventAggregator } from "aurelia";
import { getLogger } from "../common/logging";
import { IRouter } from "@aurelia/router";
import jwt_decode from "jwt-decode";
import { SessionStorage } from "./storage";
import { AuthService } from "./auth-service";

let log = getLogger("user-session");

// What the session holds is data, not accessors over the storage: an accessor that a binding observes is
// cached until something it read changes, and the storage is not something that can tell.
@inject(SessionStorage, IEventAggregator, AuthService, IRouter)
export class UserSession {
  constructor(storage, ea, auth, router) {
    this.storage = storage;
    this.ea = ea;
    this.auth = auth;
    this.router = router;
    this.readToken();
  }

  readToken() {
    this.token = this.storage.getItem("token");
    this.payload = this.decodeJwt();
    this.user = this.userOf(this.payload);
  }

  userOf(payload) {
    return {
      email: payload.sub,
      name: payload.name,
      roles: payload.roles,
      customer: payload.csn,
      isRegistrationToken: payload.isRegistrationToken,
    };
  }

  get refreshtoken() {
    return this.storage.getItem("refreshtoken");
  }

  set refreshtoken(value) {
    this.storage.setItem("refreshtoken", value);
  }

  get expires() {
    return this.storage.getItem("expires");
  }

  set expires(value) {
    this.storage.removeItem("expires");
    this.storage.setItem("expires", value);
  }

  isActive() {
    if (this.token === "undefined") return false;
    if (this.token === undefined) return false;

    const payload = this.decodeJwt();
    const ts = Math.floor(new Date().getTime() / 1000);

    if (!payload.exp) payload.exp = 0;
    return payload.exp > ts;
  }

  start(t, rt, exp) {
    if (t) {
      this.storage.setItem("token", t);
    }
    if (rt) {
      this.refreshtoken = rt;
    }
    if (exp) {
      this.expires = exp;
    }

    this.readToken();
    setTimeout(() => {
      this.refresh();
    }, this.expires * 800);
  }

  end() {
    this.storage.removeItem("token");
    this.storage.removeItem("refreshtoken");
    this.storage.removeItem("expires");
    this.readToken();
    this.ea.publish("session-ended", null)
  }

  refresh() {
    if (!this.refreshtoken) {
      return;
    }

    this.auth
      .refresh(this.refreshtoken)
      .then((data) => {
        this.start(data.access_token, data.refresh_token, data.expires_in);
      })
      .catch(() => {
        const { customer } = this.user;
        this.end();
        this.router.load(`/${customer}/logged-out`);
      });
  }

  decodeJwt() {
    const token = this.storage.getItem("token");
    if (!token) return {};
    try {
      return jwt_decode(token);
    } catch (e) {
      log.error("The stored token cannot be read", e);
      return {};
    }
  }
}
